import { Injectable } from '@angular/core';
import {
  AuthorizedPaymentMethod,
  Market,
  PaymentMethod,
  PaymentRequestDto,
  PaymentMethodViewContext
} from '@app/model/payment-request/payment-request.dto';
import { ThemeProviderService } from '../theme-provider/theme-provider.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentMethodDetailService {
  constructor(private themeProviderService: ThemeProviderService) {}

  getPaymentMethodViewContext(
    method: AuthorizedPaymentMethod,
    market: Market,
    paymentRequest: PaymentRequestDto
  ): PaymentMethodViewContext {
    const customTheme = this.themeProviderService.getValidatedTheme(paymentRequest,
      method.type
    );
    let defaultTheme: PaymentMethodViewContext;

    switch (method.type) {
      case PaymentMethod.INSTANT_DEBIT:
        defaultTheme = {
          identifier: PaymentMethod.INSTANT_DEBIT,
          title: this.getMethodTitle(PaymentMethod.INSTANT_DEBIT, market),
          subTitle: 'Extra smidigt vid större köp',
          zaverIcon: { name: 'bolt', size: 26 },
          bulletPoints: ['Pengarna dras inom 1-2 bankdagar'],
        };
        break;
      case PaymentMethod.TRUSTLY:
        defaultTheme = {
          identifier: PaymentMethod.TRUSTLY,
          title: this.getMethodTitle(PaymentMethod.TRUSTLY, market),
          subTitle: this.getMethodSubTitle(
            method,
            PaymentMethod.TRUSTLY,
            market,
            paymentRequest
          ),
          zaverIcon: { name: 'bank', size: 26 },
          bulletPoints: ['Godkänn med BankID eller dosa'],
        };
        break;

      case PaymentMethod.TOKENIZED_CREDIT_LIMIT:
        defaultTheme = {
          identifier: PaymentMethod.TOKENIZED_CREDIT_LIMIT,
          title: this.getMethodTitle(
            PaymentMethod.TOKENIZED_CREDIT_LIMIT,
            market
          ),
          subTitle: this.getMethodSubTitle(
            method,
            PaymentMethod.TOKENIZED_CREDIT_LIMIT,
            market,
            paymentRequest
          ),
          zaverIcon: { name: 'invoice', size: 26 },
          bulletPoints: ['Tokenized fejktura'],
        };
        break;

      case PaymentMethod.SWISH_EXTERNAL:
        defaultTheme = {
          identifier: PaymentMethod.SWISH_EXTERNAL,
          title: this.getMethodTitle(PaymentMethod.SWISH_EXTERNAL, market),
          subTitle: 'Perfekt för mindre belopp',
          zaverIcon: { name: 'swishSymbol', size: 26 },
          bulletPoints: ['Pengarna dras från ditt bankkonto'],
        };
        break;

      case PaymentMethod.VIPPS_EXTERNAL:
        defaultTheme = {
          identifier: PaymentMethod.VIPPS_EXTERNAL,
          title: this.getMethodTitle(PaymentMethod.VIPPS_EXTERNAL, market),
          subTitle: 'Vipps, betalingen er klar!',
          zaverIcon: { name: 'vippsSymbol', size: 20 },
          bulletPoints: ['Pengene blir trukket fra banken din'],
        };
        break;

      case PaymentMethod.INVOICE:
        defaultTheme = {
          identifier: PaymentMethod.INVOICE,
          title: this.getMethodTitle(PaymentMethod.INVOICE, market),
          subTitle: this.getMethodSubTitle(
            method,
            PaymentMethod.INVOICE,
            market,
            paymentRequest
          ),
          zaverIcon: { name: 'invoice', size: 26 }, 
          bulletPoints: this.getMethodBulletPoints(
            PaymentMethod.INVOICE,
            market,
            method
          ),
        };
        break;

      case PaymentMethod.CREDIT:
        defaultTheme = {
          identifier: PaymentMethod.CREDIT,
          title: this.getMethodTitle(PaymentMethod.CREDIT, market),
          // safe default if payment request fails to load or is loaded after the view
          subTitle: this.getMethodSubTitle(
            method,
            PaymentMethod.CREDIT,
            market,
            paymentRequest
          ),
          zaverIcon: { name: 'cake', size: 26 },
          bulletPoints: ['Betala i din egen takt'],
          // choiceTerms with "Visa SEKKI" is added by getChoiceTerms()
        };
        break;
      case PaymentMethod.CREDIT_STATIC_AMOUNT: // TODO: Different text here?
        defaultTheme = {
          identifier: PaymentMethod.CREDIT_STATIC_AMOUNT,
          title: this.getMethodTitle(
            PaymentMethod.CREDIT_STATIC_AMOUNT,
            market
          ),
          // safe default if payment request fails to load or is loaded after the view
          subTitle: this.getMethodSubTitle(
            method,
            PaymentMethod.CREDIT_STATIC_AMOUNT,
            market,
            paymentRequest
          ),
          zaverIcon: { name: 'cake', size: 26 },
          bulletPoints: ['Betala i din egen takt'],
          // choiceTerms with "Visa SEKKI" is added by getChoiceTerms()
        };
        break;
    }

    return customTheme ? { ...defaultTheme, ...customTheme } : defaultTheme;
  }

  getMethodTitle(method: PaymentMethod, market: Market) {
    switch (method) {
      // Market independent titles
      case PaymentMethod.SWISH_EXTERNAL:
        return 'Swish';
      case PaymentMethod.VIPPS_EXTERNAL:
        return 'Vipps';

      // Market dependent titles
      case PaymentMethod.INSTANT_DEBIT:
        switch (market) {
          case Market.SE:
            return 'Betala nu';
          case Market.DE:
            return 'Direkte Zahlung';
          case Market.DK:
            return 'Direkte betaling';
          case Market.NO:
            return 'Direkte betaling';
        }
      case PaymentMethod.TRUSTLY:
        switch (market) {
          case Market.SE:
            return 'Banköverföring';
          case Market.DE:
            return 'Direktüberweisung';
          case Market.DK:
            return 'Direkte bankoverførsel';
          case Market.NO:
            return 'Direkte bankoverføring';
        }
      case PaymentMethod.INVOICE:
        switch (market) {
          case Market.SE:
            return 'Faktura';
          case Market.DE:
            return 'Rechnung';
          case Market.DK:
            return 'Faktura';
          case Market.NO:
            return 'Faktura';
        }
      case PaymentMethod.CREDIT:
        switch (market) {
          case Market.SE:
            return 'Delbetalning';
          case Market.DE:
            return 'Teilzahlung';
          case Market.DK:
            return 'Delvis betaling';
          case Market.NO:
            return 'Delbetaling';
        }
      case PaymentMethod.CREDIT_STATIC_AMOUNT: // TODO add new text for sure
        switch (market) {
          case Market.SE:
            return 'Delbetalning fastpris';
          case Market.DE:
            return 'Teilzahlung (Static)';
          case Market.DK:
            return 'Delvis betaling (Static)';
          case Market.NO:
            return 'Delbetaling (Static)';
        }
      case PaymentMethod.TOKENIZED_CREDIT_LIMIT:
        switch (market) {
          case Market.DE:
            return 'Månads rechnung';
        }
    }
  }

  getMethodSubTitle(
    authorizedMethod: AuthorizedPaymentMethod,
    method: PaymentMethod,
    market: Market,
    paymentRequest: PaymentRequestDto
  ) {
    switch (method) {
      case PaymentMethod.TRUSTLY:
        switch (market) {
          case Market.SE:
            return 'Pengarna dras från din bank';
          case Market.DE:
            return 'Das Geld wird von Ihrer Bank abgebucht';
          case Market.DK:
            return 'Pengene trækkes i din bank';
          case Market.NO:
            return 'Pengene blir trukket fra banken din';
        }
      case PaymentMethod.INVOICE:
        switch (market) {
          case Market.SE:
            let invoiceFee: number =
              (authorizedMethod.promotionalStartFee ?? 0) +
              (authorizedMethod.promotionalRecurringFee ?? 0);

            if (invoiceFee > 0) {
              return `Betala inom ${authorizedMethod.promotionalDurationDays} dagar - ${invoiceFee} SEK`;
            } else {
              return `Betala inom ${authorizedMethod.promotionalDurationDays} dagar - 0 SEK`;
            }
          case Market.DE:
            return 'Später bezahlen';
          case Market.DK:
            return `Betala inom ${authorizedMethod.promotionalDurationDays} dagar - 0 DKK`;
          case Market.NO:
            return `Betal innen ${authorizedMethod.promotionalDurationDays} dager - kr 0`;
        }
      case PaymentMethod.CREDIT:
      case PaymentMethod.CREDIT_STATIC_AMOUNT: // TODO new text
        switch (market) {
          case Market.SE:
            return paymentRequest
              ? `Från ${authorizedMethod.lowestPossibleMonthlyPayment.toLocaleString(
                  'sv',
                  {
                    maximumFractionDigits: 0,
                  }
                )} SEK/mån`
              : '';
          case Market.DE:
            return paymentRequest
              ? `Von ${authorizedMethod.lowestPossibleMonthlyPayment.toLocaleString(
                  'de',
                  {
                    maximumFractionDigits: 0,
                  }
                )} € / Monat`
              : '';
          case Market.DK:
            return paymentRequest
              ? `Fra ${authorizedMethod.lowestPossibleMonthlyPayment.toLocaleString(
                  'dk',
                  {
                    maximumFractionDigits: 0,
                  }
                )} DKK/md`
              : '';
          case Market.NO:
            return paymentRequest
              ? `Fra kr ${authorizedMethod.lowestPossibleMonthlyPayment.toLocaleString(
                  'nb',
                  {
                    maximumFractionDigits: 0,
                  }
                )}/mnd`
              : '';
        }

      case PaymentMethod.TOKENIZED_CREDIT_LIMIT:
        switch (market) {
          case Market.DE:
            return 'En rechnung i månaden!';
        }
    }
  }

  getMethodBulletPoints(
    method: PaymentMethod,
    market: Market,
    authorizedPaymentMethod: AuthorizedPaymentMethod
  ): string[] {
    let result: string[] = [];
    switch (method) {
      case PaymentMethod.INVOICE:
        switch (market) {
          case Market.SE:
            const invoiceFee: number =
              (authorizedPaymentMethod?.promotionalStartFee ?? 0) +
              (authorizedPaymentMethod?.promotionalRecurringFee ?? 0);

            if (invoiceFee > 0) {
              result.push(`Betala senare, i lugn och ro.`);
            } else {
              result.push(`Betala senare, helt gratis!`);
            }

            return result;
          case Market.DE:
            result.push('In 14 Tagen bezahlen - 0 EUR');
            return result;
          case Market.DK:
            result.push('Betal senere, helt gratis');
            return result;
          case Market.NO:
            result.push(`Betal senere. Helt gratis.`);
            return result;
        }
      default:
        console.error(
          'Attempted to fetch bullet points for unimplemented method'
        );
    }
  }

  setupMarketBasedLabels(market: Market) {
    let ctaLabel = null;
    switch (market) {
      case Market.SE:
        ctaLabel = 'Fortsätt';
        break;
      case Market.DE:
        ctaLabel = 'Weiter';
        break;
      case Market.DK:
        ctaLabel = 'Fortsette';
        break;
      case Market.NO:
        ctaLabel = 'Neste';
        break;
      default:
        console.error('Unimplemented market: ', market);
    }
    return ctaLabel;
  }
}
